import { useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { PanelShell, StatGrid, StatTile, PanelUnavailable, FindingList } from "./PanelShell";

const AXIS_ORDER = ["roll", "pitch", "yaw"];

/**
 * The control band, 1–30 Hz, where a tuning problem shows up as one coherent
 * spike rather than a raised noise floor.
 *
 * Deliberately a different view from the Filters tab: that one asks whether the
 * notch covers the vibration, this one asks whether the aircraft is ringing.
 * A 1 Hz oscillation is invisible there and unmistakable here.
 */
const OscillationPanel = ({ module }) => {
  const theme = useTheme();
  const axes = module?.panel?.axes || {};
  const available = AXIS_ORDER.filter((a) => axes[a]);
  const [axis, setAxis] = useState(available[0] || "roll");

  if (available.length === 0) {
    return <PanelUnavailable reason={module?.skip_reason || "No gyro data to analyse for oscillation."} />;
  }

  const panel = axes[axis] || axes[available[0]];
  const rows = panel.frequencies.map((f, i) => ({ freq: f, amp: panel.amplitudes[i] }));
  const [bandLow, bandHigh] = panel.band_hz;

  // Prominence is a peak-to-median ratio, so the thresholds are the same
  // numbers the backend classifies on.
  const tone = panel.prominence > 35 ? "bad" : panel.prominence > 18 ? "warn" : "good";

  return (
    <PanelShell
      title="Oscillation"
      subtitle={`Control band ${bandLow}–${bandHigh} Hz · gyro logged at ${panel.sample_rate_hz} Hz`}
      health={module.health}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={axis}
        onChange={(e, value) => value && setAxis(value)}
        sx={{ mb: 2 }}
      >
        {available.map((a) => (
          <ToggleButton key={a} value={a} sx={{ textTransform: "capitalize", px: 2 }}>
            {a}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <StatGrid>
        <StatTile label="Dominant frequency" value={panel.peak_hz ?? "—"} unit={panel.peak_hz ? "Hz" : ""} />
        <StatTile
          label="Prominence"
          value={`${panel.prominence}×`}
          tone={tone}
          hint="How far the peak stands above the median of the band. Below ~10× there is no oscillation to speak of."
        />
        <StatTile label="Search band" value={`${bandLow}–${bandHigh}`} unit="Hz" />
      </StatGrid>

      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="freq"
              type="number"
              domain={[0, bandHigh]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              label={{ value: "Hz", position: "insideBottomRight", offset: -2, fontSize: 10 }}
            />
            <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
            <Tooltip
              contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
              labelFormatter={(l) => `${Number(l).toFixed(2)} Hz`}
              formatter={(v) => [Number(v).toFixed(4), "amplitude"]}
            />
            {/* The band a rate loop actually rings in. */}
            <ReferenceArea x1={2} x2={Math.min(10, bandHigh)} fill="#3a8eff" fillOpacity={0.06} />
            {panel.peak_hz && (
              <ReferenceLine
                x={panel.peak_hz}
                stroke="#FF7A2F"
                strokeDasharray="4 4"
                label={{ value: `${panel.peak_hz} Hz`, fill: "#FF7A2F", fontSize: 10, position: "top" }}
              />
            )}
            <Line type="monotone" dataKey="amp" stroke="#E7C25B" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
        One tall spike means the aircraft is ringing at that frequency; a bumpy spectrum with no clear winner
        means it is not. The shaded region is where a rate loop typically oscillates. If the peak moves with
        throttle rather than staying put, it is a mechanical resonance and belongs to the Filters tab instead.
      </Typography>

      <FindingList findings={module.findings} />
    </PanelShell>
  );
};

export default OscillationPanel;
